import { useEffect, useRef, useState } from "react";
import { useShowAlert } from "../context/AlertContext";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import InterRegular from "./font/Inter_24pt-Regular-normal";
import InterBold from "./font/Inter_24pt-Bold-normal";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import uRLs from "../utils/baseUtils";
import { FormatedSaleData } from "../utils/GloabalSaleData";
import InvoiceLayout from "./common/InvoiceLayout ";
import InvoiceWrapper from "./common/InvoiceWrapper";

const ServiceInvoice = ({ isData }) => {
  const showAlert = useShowAlert();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  // const { useEscapeKey } = useContextApi();
  const [download, setDownload] = useState(false);
  const [data, setData] = useState({});
  const globalData = FormatedSaleData(data);
  const actualPaidAmount = globalData?.grandtotal - globalData?.balanceDue;
  // const os = getOS();

  const selectedTheme = isData?.storeSettings?.invoiceSettings?.template;
  const terms = isData?.storeSettings?.invoiceSettings?.termsAndConditions;
  
  const formatTrackMode = (salemode) => {
    switch (salemode?.toUpperCase()) {
      case "BILL":
        return "BILL RECIEPT";
      case "INVOICE":
        return "TAX INVOICE";
      default:
        return salemode;
    }
  };

  const saleDate = data?.saledate;
  const timezone = data?.saleby?.timezone;

  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  let createdTime = "--:--";
  if (data?.createdAt) {
    const zoned = DateConverter.convertToTimeZone(data.createdAt, timezone);
    createdTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  const loadImageDimensions = (base64Src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = base64Src;
    });
  };

  const urlToBase64 = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const joinAddress = (...lines) =>
    lines
      .map((l) => l?.trim())
      .filter(Boolean)
      .join(", ");

  const generateInvoice1 = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const PADDING = 5;
    const MARGIN_LEFT = PADDING;
    const MARGIN_RIGHT = pageW - PADDING;
    let y = PADDING + 5;

    // --- 1. HEADER SECTION ---
    // Logo (Top Left)
    if (globalData?.salefrom?.image) {
      try {
        const imagePath =
          uRLs?.IMAGE_URL + "profile/" + globalData?.salefrom?.image;
        const base64 = await urlToBase64(imagePath);

        // 1. Get real dimensions
        const { width, height } = await loadImageDimensions(base64);

        // 2. Define your maximum allowed size for the logo area
        const maxWidth = 30; // Slightly larger for better readability
        const maxHeight = 12;

        // 3. Calculate the ratio that fits without squeezing
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const finalWidth = width * ratio;
        const finalHeight = height * ratio;

        // 4. Center the image vertically in the 12-unit high 'maxHeight' space
        const adjustedY = y - 5 + (maxHeight - finalHeight) / 2;

        // 5. Add the image with dynamic dimensions
        doc.addImage(
          base64,
          "PNG",
          MARGIN_LEFT,
          adjustedY,
          finalWidth,
          finalHeight
        );
      } catch (err) {
        // Fallback: If image fails, show company title
        console.warn("Logo failed to load:", err);
        doc.setFont("Inter-Bold", "bold");
        doc.setFontSize(14);
        doc.text(globalData?.salefrom?.title || "COMPANY LOGO", MARGIN_LEFT, y);
      }
    }

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20, 40, 100);
    doc.text("TAX INVOICE", MARGIN_RIGHT, y, { align: "right" });
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(`Invoice #: ${globalData.saleno || ""}`, MARGIN_RIGHT, y, {
      align: "right",
    });

    // --- 2. SELLER DETAILS (Left) ---
    y += 6;
    doc.setFontSize(10);
    doc.setFont("Inter-Bold", "bold");
    doc.text(globalData?.salefrom?.title || "", MARGIN_LEFT, y);

    y += 5;
    doc.setFontSize(8);
    doc.text(`GSTIN ${globalData?.salefrom?.gstnumber || ""}`, MARGIN_LEFT, y);

    y += 4;
    doc.setFont("Inter-Regular", "normal");
    const sellerAddress = joinAddress(
      globalData?.salefrom?.addressline1,
      globalData?.salefrom?.addressline2,
      globalData?.salefrom?.district,
      globalData?.salefrom?.state,
      globalData?.salefrom?.pincode
    );
    const sellerAddrLines = doc.splitTextToSize(sellerAddress, 70);
    doc.text(sellerAddrLines, MARGIN_LEFT, y);

    y += sellerAddrLines.length * 4;
    const drawBoldLabelNormalValue = (label, value, x, currentY) => {
      // 1. Set to Bold
      doc.setFont("helvetica", "bold");
      const labelText = `${label} `; // Removed colon if you want it exactly like the image
      doc.text(labelText, x, currentY);

      // 2. Measure the bold text width
      const labelWidth = doc.getTextWidth(labelText);

      // 3. Set to Normal
      doc.setFont("helvetica", "normal");
      doc.text(String(value || ""), x + labelWidth, currentY);
    };
    drawBoldLabelNormalValue(
      "Mobile",
      globalData?.salefrom?.phone,
      MARGIN_LEFT,
      y
    );

    if (globalData?.salefrom?.pannumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "PAN",
        globalData.salefrom.pannumber,
        MARGIN_LEFT,
        y
      );
    }

    if (globalData?.salefrom?.fassi) {
      y += 4;
      drawBoldLabelNormalValue(
        "FSSAI",
        globalData.salefrom.fassi,
        MARGIN_LEFT,
        y
      );
    }

    if (globalData?.salefrom?.cinnumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "CIN",
        globalData.salefrom.cinnumber,
        MARGIN_LEFT,
        y
      );
    }

    const hasCustomer = !!globalData?.saleto;
    const hasDate = !!globalData?.saledate;
    const hasSupply = !!globalData?.saleto?.state;

    let hasBillSection = hasCustomer || hasDate || hasSupply;

    if (hasBillSection) {
      y += 6;

      const col2X = 140;

      /* --- LEFT: BILL TO --- */
      if (hasCustomer ) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Bill To:", MARGIN_LEFT, y);

        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(globalData?.saleto?.name || "", MARGIN_LEFT, y + 4);

        if (globalData?.saleto?.gstnumber) {
          doc.text(`GSTIN: ${globalData.saleto.gstnumber}`, MARGIN_LEFT, y + 8);
        }

        // Address
        const custAddress = [
          globalData?.saleto?.addressline1,
          globalData?.saleto?.addressline2,
          globalData?.saleto?.district,
          globalData?.saleto?.state,
          globalData?.saleto?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        let leftY = y + 12;

        if (custAddress) {
          const lines = doc.splitTextToSize(custAddress, 80);
          doc.setFont("Inter-Regular", "normal");
          doc.text(lines, MARGIN_LEFT, leftY);
          leftY += lines.length * 4;
        }

        // Contact
        if (globalData?.saleto?.phone || globalData?.saleto?.email) {
          const contact = `Ph: ${globalData?.saleto?.phone || ""} ${
            globalData?.saleto?.email
              ? "| Email: " + globalData.saleto.email
              : ""
          }`;
          doc.text(contact, MARGIN_LEFT, leftY);
          leftY += 4;
        }

        var leftEndY = leftY;
      }

      /* --- RIGHT: DATE / TIME / SUPPLY --- */
      let rightY = y;

      if (hasDate) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Invoice Date:", col2X, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(
          DateConverter.ISOToDisplayFormat(globalData?.saledate).date,
          MARGIN_RIGHT,
          rightY,
          { align: "right" }
        );

        rightY += 4;

        doc.setFont("Inter-Bold", "bold");
        doc.text("Invoice Time:", col2X, rightY);

        doc.setFont("Inter-Regular", "normal");
        const invTime = DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(globalData?.saledate, timezone)
        ).time;

        doc.text(invTime, MARGIN_RIGHT, rightY, { align: "right" });

        rightY += 4;
      }

      if (hasSupply) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Place of Supply:", col2X, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(globalData.saleto.state, MARGIN_RIGHT, rightY, {
          align: "right",
        });

        rightY += 4;
      }

      /* --- FINAL HEIGHT FIX --- */
      const finalY = Math.max(leftEndY || y, rightY);

      y = finalY + 2;
    }
    autoTable(doc, {
      startY: y,
      theme: "plain",
      margin: { top: PADDING, left: PADDING, right: PADDING, bottom: PADDING }, // Balanced
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        textColor: [0, 0, 0],
      },
      headStyles: {
        font: "Inter-Bold",
        fontStyle: "bold",
        fillColor: false,
        textColor: "#000",
        //  textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0], // White text
        lineWidth: { bottom: 0.5 },
        lineColor: selectedTheme?.templateTheme?.borderColor,
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "left" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 30, halign: "right" },
        3: { cellWidth: 20, halign: "right" },
        4: { cellWidth: 30, halign: "right" },
      },
      head: [["#", "Item", "Selling Price", "Qty", "Amount"]],
      body: globalData.saleitems.map((it, i) => [
        i + 1,
        it.title,
        `₹${it.baseprice}`,
        it.quantity,
        `₹${it.lineGrandTotal}`,
      ]),
      didParseCell: function (data) {
        // Header alignment
        if (data.section === "head") {
          const colId = data.column.index;
          if (colId === 0) data.cell.styles.halign = "left";
          if (colId === 2 || colId === 3 || colId === 4 || colId === 5)
            data.cell.styles.halign = "right";
        }
        if (data.section === "body") {
          // Light gray divider lines
          data.cell.styles.lineWidth = { bottom: 0.1 };
        }
      },
    });

    y = doc.lastAutoTable.finalY + 5;

    if (y + PADDING > pageH - 20) {
      doc.addPage();
      y = PADDING + 5;
    }
    const footerTop = y;
    let leftY = footerTop;
    let rightY = footerTop;

    // --- 1. LEFT COLUMN: Bank Details ---
    doc.setFontSize(9);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    if (globalData?.storeSettings?.paymentSettings?.bankDetails) {
      doc.text("Bank Details:", MARGIN_LEFT, leftY);
      leftY += 5;

      const bankInfo = [
        [
          "Name:",
          globalData?.storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "",
        ],
        [
          "Account #:",
          globalData?.storeSettings?.paymentSettings?.bankDetails?.accountNo || "",
        ],
        ["IFSC:", globalData?.storeSettings?.paymentSettings?.bankDetails?.ifsc || ""],
        ["UPI ID:", globalData?.storeSettings?.paymentSettings?.upiId || ""],
      ];

      bankInfo.forEach(([label, value]) => {
        doc.setFont("Inter-Regular", "normal");
        doc.text(label, MARGIN_LEFT, leftY);
        doc.setFont("Inter-Bold", "bold");
        doc.text(String(value || ""), MARGIN_LEFT + 20, leftY);
        leftY += 4;
      });
    }
    const upiId = globalData?.storeSettings?.paymentSettings?.upiId || "";
    doc.setFontSize(8);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
      globalData?.storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || ""
    )}&cu=INR`;
    const qrX = pageW / 2 - 15;
    if (upiId && upiId.trim() !== "") {
      const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
        errorCorrectionLevel: "L",
        scale: 4,
        margin: 1,
      });
      doc.addImage(qrCodeDataUrl, "JPEG", qrX, footerTop - 3, 20, 20);
    }
    const summaryX = 140;

    // Row 1: Service Charges (Normal Font)
    // doc.setFont("Inter-Regular", "normal");
    // doc.text("Service Charges", summaryX, rightY);
    // doc.setTextColor(0);
    // doc.text(formatCurrency(data.serviceCharges || 0), MARGIN_RIGHT, rightY, { align: "right" });
    // rightY += 4;

    // // SAC Code (Small font under Service Charges)
    // doc.setFontSize(7);
    // doc.text("SAC:", summaryX + 10, rightY); // Offset slightly to the right
    // rightY += 5;
    const isInterState =
      globalData?.salefrom?.statecode &&
      globalData?.saleto?.statecode &&
      globalData?.salefrom?.statecode !== globalData?.saleto?.statecode;

    // Rows 2-4: Taxable & GST (Bold Font)
    const summaryItems = [["Taxable Amount", `₹${globalData?.totalbaseprice}`]];
    if (isInterState) {
      summaryItems.push(["IGST ", `₹${globalData?.igstamount}`]);
    } else {
      summaryItems.push(["CGST ", `₹${globalData?.cgstamount}`]);
      summaryItems.push(["SGST ", `₹${globalData?.sgstamount}`]);
    }

    doc.setFontSize(9);
    summaryItems.forEach(([label, value]) => {
      doc.setFont("Inter-Bold", "bold");
      doc.setTextColor(0);
      doc.text(label, summaryX, rightY);
      doc.text(value, MARGIN_RIGHT, rightY, { align: "right" });
      rightY += 5;
    });
    rightY += 2;
    // --- 4. THE TOTAL BOX ---
    const internalPadding = 2; // Adjust this value to get the exact gap you want
    doc.setFillColor(240, 245, 255);
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    doc.setLineWidth(0.4);
    doc.rect(summaryX - 2, rightY - 4, MARGIN_RIGHT - summaryX + 2, 8, "FD");
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(11);
    doc.text("Total", summaryX, rightY + 1.5);
    doc.text(
      `₹${globalData.grandtotal}`,
      MARGIN_RIGHT - internalPadding, // This moves the amount away from the line
      rightY + 1.5,
      { align: "right" }
    );
    // doc.text(formatCurrency((CurrencyConverter.toCurrency(data.grandtotal|| 0, "INR")), "INR", true, true), MARGIN_RIGHT, rightY + 1.5, { align: "right" });
    rightY += 8;
    if (leftY > pageH - 20) {
      doc.addPage();
      leftY = PADDING + 5;
      rightY = PADDING + 5;
    }

    doc.setFontSize(9);
    doc.text("Amount Payable:", summaryX, rightY);
    doc.text(`₹${globalData?.balanceDue}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    rightY += 5;
    doc.text("Amount Paid:", summaryX, rightY);
    doc.text(`₹${globalData?.paidamount}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const amountWords = `Total amount (in words) ${
      globalData?.amountinwords || ""
    }`;
    // doc.text(amountWords, MARGIN_LEFT, wordsY);
    const wordLines = doc.splitTextToSize(amountWords, qrX + 25);
    doc.text(wordLines, MARGIN_LEFT, leftY + 2);

    leftY += wordLines.length * 4 + 3;

    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);
    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);
    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);
    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    let paymentMethodsHeight = 0;
    if (cashPaid > 0) paymentMethodsHeight += 4;
    if (upiPaid > 0) paymentMethodsHeight += 4;
    if (cardPaid > 0) paymentMethodsHeight += 4;
    if (onlinePaid > 0) paymentMethodsHeight += 4;
    const totalNeededHeight = paymentMethodsHeight + wordLines.length * 4 + 10;
    if (leftY + totalNeededHeight > pageH - 20) {
      doc.addPage();
      leftY = PADDING + 5;
      rightY = PADDING + 5;
    }
    doc.setFont("Inter-Bold", "bold");
    if (globalData?.cashPaid > 0) {
      const cashText = `₹${globalData?.cashPaid} Paid via Cash `;
      doc.text(cashText, MARGIN_LEFT, leftY, { align: "left" });
    }
    if (globalData?.upiPaid > 0) {
      leftY += 4;
      const cashText = `₹${globalData?.upiPaid} Paid via UPI `;
      doc.text(cashText, MARGIN_LEFT, leftY, { align: "left" });
    }
    if (globalData?.cardPaid > 0) {
      leftY += 4;
      const cashText = `₹${globalData?.cardPaid} Paid via card `;
      doc.text(cashText, MARGIN_LEFT, leftY, { align: "left" });
    }
    if (globalData?.onlinePaid > 0) {
      leftY += 4;
      const cashText = `₹${globalData?.onlinePaid} Paid via Online `;
      doc.text(cashText, MARGIN_LEFT, leftY, { align: "left" });
    }
    doc.setFont("Inter-Regular", "normal");

    y = Math.max(leftY, rightY) + 8;
    if (y > pageH - 40) {
      doc.addPage();
      y = PADDING + 5;
    }
    let rowStartY = y;
    if (terms?.length > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      // Page break check
      if (rowStartY > pageH - 25) {
        doc.addPage();
        rowStartY = PADDING + 5;
      }

      doc.text("Terms & Conditions", PADDING, rowStartY);
      doc.setTextColor(0);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);

      const termLines = doc.splitTextToSize(terms, pageW - 50); // Limit width to half page
      let currentTermY = rowStartY + 5;

      termLines.forEach((line) => {
        if (currentTermY > pageH - 20) {
          doc.addPage();
          currentTermY = 10;
        }
        doc.text(line, PADDING, currentTermY);
        currentTermY += 5;
      });

      // Update global y based on how long the terms were
      y = currentTermY;
    }

    if (globalData?.storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + globalData?.storeSettings.invoiceSettings.signature;
        const base64 = await urlToBase64(signimg);
        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 35;
        const maxHeight = 20;
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgW = width * ratio;
        const imgH = height * ratio;

        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          MARGIN_RIGHT - imgW,
          rowStartY, // Start at the same Y as the Terms header
          imgW,
          imgH
        );
      } catch {
        console.warn("Signature load failed");
      }
    }
    doc.setTextColor(0);
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(10);
    doc.text("Authorized Signatory", MARGIN_RIGHT, rowStartY + 13, {
      align: "right",
    });
    if (globalData?.storeSettings?.negobillBranding) {
      const y = pageH - 5;
      const rightX = pageW / 2 - 10;

      // Text parts
      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(8);

      // Measure widths
      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);

      doc.setFont("Inter-Bold", "bold");

      // Start X so whole string is right-aligned
      const startX = rightX;

      // Draw normal part
      doc.setFont("Inter-Regular", "normal");
      doc.text(normalText, startX, y, {
        align: "center",
      });

      // Draw bold part
      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, startX + 1 + normalW, y, {
        align: "center",
      });
    }
    setDownload(false);
    return doc;
  };

  const generateBill1 = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const PADDING = 5;
    const MARGIN_LEFT = PADDING;
    const MARGIN_RIGHT = pageW - PADDING;
    let y = PADDING + 5;

    // --- 1. HEADER SECTION ---
    // Logo (Top Left)
    if (globalData?.salefrom?.image) {
      try {
        const imagePath =
          uRLs?.IMAGE_URL + "profile/" + globalData?.salefrom?.image;
        const base64 = await urlToBase64(imagePath);

        // 1. Get real dimensions
        const { width, height } = await loadImageDimensions(base64);

        // 2. Define your maximum allowed size for the logo area
        const maxWidth = 30; // Slightly larger for better readability
        const maxHeight = 12;

        // 3. Calculate the ratio that fits without squeezing
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const finalWidth = width * ratio;
        const finalHeight = height * ratio;

        // 4. Center the image vertically in the 12-unit high 'maxHeight' space
        const adjustedY = y - 5 + (maxHeight - finalHeight) / 2;

        // 5. Add the image with dynamic dimensions
        doc.addImage(
          base64,
          "PNG",
          MARGIN_LEFT,
          adjustedY,
          finalWidth,
          finalHeight
        );
      } catch (err) {
        // Fallback: If image fails, show company title
        console.warn("Logo failed to load:", err);
        doc.setFont("Inter-Bold", "bold");
        doc.setFontSize(14);
        doc.text(globalData?.salefrom?.title || "COMPANY LOGO", MARGIN_LEFT, y);
      }
    }

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20, 40, 100);
    doc.text("BILL RECEIPT", MARGIN_RIGHT, y, { align: "right" });
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(`Bill #: ${globalData.saleno || ""}`, MARGIN_RIGHT, y, {
      align: "right",
    });

    // --- 2. SELLER DETAILS (Left) ---
    y += 6;
    doc.setFontSize(10);
    doc.setFont("Inter-Bold", "bold");
    doc.text(globalData?.salefrom?.title || "", MARGIN_LEFT, y);

    y += 5;
    doc.setFontSize(8);
    doc.text(`GSTIN ${globalData?.salefrom?.gstnumber || ""}`, MARGIN_LEFT, y);

    y += 4;
    doc.setFont("Inter-Regular", "normal");
    const sellerAddress = joinAddress(
      globalData?.salefrom?.addressline1,
      globalData?.salefrom?.addressline2,
      globalData?.salefrom?.district,
      globalData?.salefrom?.state,
      globalData?.salefrom?.pincode
    );
    const sellerAddrLines = doc.splitTextToSize(sellerAddress, 70);
    doc.text(sellerAddrLines, MARGIN_LEFT, y);

    y += sellerAddrLines.length * 4;
    const drawBoldLabelNormalValue = (label, value, x, currentY) => {
      // 1. Set to Bold
      doc.setFont("helvetica", "bold");
      const labelText = `${label} `; // Removed colon if you want it exactly like the image
      doc.text(labelText, x, currentY);

      // 2. Measure the bold text width
      const labelWidth = doc.getTextWidth(labelText);

      // 3. Set to Normal
      doc.setFont("helvetica", "normal");
      doc.text(String(value || ""), x + labelWidth, currentY);
    };
    drawBoldLabelNormalValue(
      "Mobile",
      globalData?.salefrom?.phone,
      MARGIN_LEFT,
      y
    );

    if (globalData?.salefrom?.pannumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "PAN",
        globalData.salefrom.pannumber,
        MARGIN_LEFT,
        y
      );
    }

    if (globalData?.salefrom?.fassi) {
      y += 4;
      drawBoldLabelNormalValue(
        "FSSAI",
        globalData.salefrom.fassi,
        MARGIN_LEFT,
        y
      );
    }

    if (globalData?.salefrom?.cinnumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "CIN",
        globalData.salefrom.cinnumber,
        MARGIN_LEFT,
        y
      );
    }

    // --- 3. BILL TO & DATES ---
    const hasCustomer = !!globalData?.saleto;
    const hasDate = !!globalData?.saledate;
    const hasSupply = !!globalData?.saleto?.state;

    let hasBillSection = hasCustomer || hasDate || hasSupply;

    if (hasBillSection) {
      y += 6;

      const col2X = 140;

      /* --- LEFT: BILL TO --- */
      if (hasCustomer ) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Bill To:", MARGIN_LEFT, y);

        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(globalData?.saleto?.name || "", MARGIN_LEFT, y + 4);

        if (globalData?.saleto?.gstnumber) {
          doc.text(`GSTIN: ${globalData.saleto.gstnumber}`, MARGIN_LEFT, y + 8);
        }

        // Address
        const custAddress = [
          globalData?.saleto?.addressline1,
          globalData?.saleto?.addressline2,
          globalData?.saleto?.district,
          globalData?.saleto?.state,
          globalData?.saleto?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        let leftY = y + 12;

        if (custAddress) {
          const lines = doc.splitTextToSize(custAddress, 80);
          doc.setFont("Inter-Regular", "normal");
          doc.text(lines, MARGIN_LEFT, leftY);
          leftY += lines.length * 4;
        }

        // Contact
        if (globalData?.saleto?.phone || globalData?.saleto?.email) {
          const contact = `Ph: ${globalData?.saleto?.phone || ""} ${
            globalData?.saleto?.email
              ? "| Email: " + globalData.saleto.email
              : ""
          }`;
          doc.text(contact, MARGIN_LEFT, leftY);
          leftY += 4;
        }

        var leftEndY = leftY;
      }

      /* --- RIGHT: DATE / TIME / SUPPLY --- */
      let rightY = y;

      if (hasDate) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Bill Date:", col2X, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(
          DateConverter.ISOToDisplayFormat(globalData?.saledate).date,
          MARGIN_RIGHT,
          rightY,
          { align: "right" }
        );

        rightY += 4;

        doc.setFont("Inter-Bold", "bold");
        doc.text("Bill Time:", col2X, rightY);

        doc.setFont("Inter-Regular", "normal");
        const invTime = DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(globalData?.saledate, timezone)
        ).time;

        doc.text(invTime, MARGIN_RIGHT, rightY, { align: "right" });

        rightY += 4;
      }

      if (hasSupply) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Place of Supply:", col2X, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(globalData.saleto.state, MARGIN_RIGHT, rightY, {
          align: "right",
        });

        rightY += 4;
      }

      /* --- FINAL HEIGHT FIX --- */
      const finalY = Math.max(leftEndY || y, rightY);

      y = finalY + 2;
    }
    autoTable(doc, {
      startY: y,
      theme: "plain",
      margin: { top: PADDING, left: PADDING, right: PADDING, bottom: PADDING }, // Balanced
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        textColor: [0, 0, 0],
      },
      headStyles: {
        font: "Inter-Bold",
        fontStyle: "bold",
        fillColor: false,
        textColor: "#000",
        //  textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0], // White text
        lineWidth: { bottom: 0.5 },
        lineColor: selectedTheme?.templateTheme?.borderColor,
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "left" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 30, halign: "right" },
        3: { cellWidth: 20, halign: "right" },
        4: { cellWidth: 30, halign: "right" },
      },
      head: [["#", "Item", "Selling Price", "Qty", "Amount"]],
      body: globalData?.saleitems.map((it, i) => [
        i + 1,
        it.title,
        `₹${it.baseprice}`,
        it.quantity,
        `₹${it.lineGrandTotal}`,
      ]),
      didParseCell: function (data) {
        // Header alignment
        if (data.section === "head") {
          const colId = data.column.index;
          if (colId === 0) data.cell.styles.halign = "left";
          if (colId === 2 || colId === 3 || colId === 4 || colId === 5)
            data.cell.styles.halign = "right";
        }
        if (data.section === "body") {
          // Light gray divider lines
          data.cell.styles.lineWidth = { bottom: 0.1 };
        }
      },
    });

    y = doc.lastAutoTable.finalY + 5;
    if (y + PADDING > pageH - 20) {
      doc.addPage();
      y = PADDING + 5;
    }
    const footerTop = y;

    let leftY = footerTop;
    let rightY = footerTop;

    // --- 1. LEFT COLUMN: Bank Details ---
    doc.setFontSize(9);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    if (globalData?.storeSettings?.paymentSettings?.bankDetails) {
      doc.text("Bank Details:", MARGIN_LEFT, leftY);
      leftY += 5;

      const bankInfo = [
        [
          "Name:",
          globalData?.storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "",
        ],
        [
          "Account #:",
          globalData?.storeSettings?.paymentSettings?.bankDetails?.accountNo || "",
        ],
        ["IFSC:", globalData?.storeSettings?.paymentSettings?.bankDetails?.ifsc || ""],
        ["UPI ID:", globalData?.storeSettings?.paymentSettings?.upiId || ""],
      ];

      bankInfo.forEach(([label, value]) => {
        doc.setFont("Inter-Regular", "normal");
        doc.text(label, MARGIN_LEFT, leftY);
        doc.setFont("Inter-Bold", "bold");
        doc.text(String(value || ""), MARGIN_LEFT + 20, leftY);
        leftY += 4;
      });
    }
    const upiId = globalData?.storeSettings?.paymentSettings?.upiId || "";
    doc.setFontSize(8);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
      globalData?.storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || ""
    )}&cu=INR`;
    const qrX = pageW / 2 - 15;
    if (upiId && upiId.trim() !== "") {
      const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
        errorCorrectionLevel: "L",
        scale: 4,
        margin: 1,
      });
      doc.addImage(qrCodeDataUrl, "JPEG", qrX, footerTop - 3, 20, 20);
    }
    const summaryX = 140;
    // Rows 2-4: Taxable & GST (Bold Font)
    const summaryItems = [["Taxable Amount", `₹${globalData?.totalbaseprice}`]];

    doc.setFontSize(9);
    summaryItems.forEach(([label, value]) => {
      doc.setFont("Inter-Bold", "bold");
      doc.setTextColor(0);
      doc.text(label, summaryX, rightY);
      doc.text(value, MARGIN_RIGHT, rightY, { align: "right" });
      rightY += 5;
    });
    rightY += 2;
    // --- 4. THE TOTAL BOX ---
    doc.setFillColor(240, 245, 255);
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    doc.setLineWidth(0.4);
    doc.rect(summaryX - 2, rightY - 4, MARGIN_RIGHT - summaryX + 2, 8, "FD");

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(11);
    doc.text("Total", summaryX, rightY + 1.5);
    doc.text(
      `₹${globalData?.grandtotal}`,
      MARGIN_RIGHT - 2, // This moves the amount away from the line
      rightY + 1.5,
      { align: "right" }
    );
    rightY += 8;

    doc.setFontSize(9);
    doc.text("Amount Payable:", summaryX, rightY);
    doc.text(`₹${globalData?.balanceDue}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    rightY += 5;
    doc.text("Amount Paid:", summaryX, rightY);
    doc.text(`₹${globalData?.paidamount}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const amountWords = `Total amount (in words) ${
      globalData?.amountinwords || ""
    }`;
    // doc.text(amountWords, MARGIN_LEFT, wordsY);
    const wordLines = doc.splitTextToSize(amountWords, qrX + 25);
    doc.text(wordLines, MARGIN_LEFT, leftY + 2);

    leftY += wordLines.length * 4 + 3;

    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);
    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);
    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);
    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);
    let paymentMethodsHeight = 0;
    if (cashPaid > 0) paymentMethodsHeight += 4;
    if (upiPaid > 0) paymentMethodsHeight += 4;
    if (cardPaid > 0) paymentMethodsHeight += 4;
    if (onlinePaid > 0) paymentMethodsHeight += 4;
    const totalNeededHeight = paymentMethodsHeight + wordLines.length * 4 + 10;
    if (leftY + totalNeededHeight > pageH - 20) {
      doc.addPage();
      leftY = PADDING + 5;
      rightY = PADDING + 5;
    }
    doc.setFont("Inter-Bold", "bold");
    if (globalData?.cashPaid > 0) {
      const cashText = `₹${globalData?.cashPaid} Paid via Cash `;
      doc.text(cashText, MARGIN_LEFT, leftY, { align: "left" });
    }
    if (globalData?.upiPaid > 0) {
      leftY += 4;
      const cashText = `₹${globalData?.upiPaid} Paid via UPI `;
      doc.text(cashText, MARGIN_LEFT, leftY, { align: "left" });
    }
    if (globalData?.cardPaid > 0) {
      leftY += 4;
      const cashText = `₹${globalData?.cardPaid} Paid via card `;
      doc.text(cashText, MARGIN_LEFT, leftY, { align: "left" });
    }
    if (globalData?.onlinePaid > 0) {
      leftY += 4;
      const cashText = `₹${globalData?.onlinePaid} Paid via Online `;
      doc.text(cashText, MARGIN_LEFT, leftY, { align: "left" });
    }
    doc.setFont("Inter-Regular", "normal");

    y = Math.max(leftY, rightY) + 8;
    if (y > pageH - 40) {
      doc.addPage();
      y = PADDING + 5;
    }
    let rowStartY = y;
    if (terms?.length > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      // Page break check
      if (rowStartY > pageH - 25) {
        doc.addPage();
        rowStartY = PADDING + 5;
      }

      doc.text("Terms & Conditions", PADDING, rowStartY);
      doc.setTextColor(0);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);

      const termLines = doc.splitTextToSize(terms, pageW - 50); // Limit width to half page
      let currentTermY = rowStartY + 5;

      termLines.forEach((line) => {
        if (currentTermY > pageH - 20) {
          doc.addPage();
          currentTermY = 10;
        }
        doc.text(line, PADDING, currentTermY);
        currentTermY += 5;
      });

      // Update global y based on how long the terms were
      y = currentTermY;
    }

    if (globalData?.storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + globalData?.storeSettings.invoiceSettings.signature;
        const base64 = await urlToBase64(signimg);
        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 35;
        const maxHeight = 20;
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgW = width * ratio;
        const imgH = height * ratio;

        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          MARGIN_RIGHT - imgW,
          rowStartY, // Start at the same Y as the Terms header
          imgW,
          imgH
        );
      } catch {
        console.warn("Signature load failed");
      }
    }
    doc.setTextColor(0);
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(10);
    doc.text("Authorized Signatory", MARGIN_RIGHT, rowStartY + 13, {
      align: "right",
    });
    if (globalData?.storeSettings?.negobillBranding) {
      const y = pageH - 5;
      const rightX = pageW / 2 - 10;

      // Text parts
      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(8);

      // Measure widths
      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);

      doc.setFont("Inter-Bold", "bold");

      // Start X so whole string is right-aligned
      const startX = rightX;

      // Draw normal part
      doc.setFont("Inter-Regular", "normal");
      doc.text(normalText, startX, y, {
        align: "center",
      });

      // Draw bold part
      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, startX + 1 + normalW, y, {
        align: "center",
      });
    }
    setDownload(false);
    return doc;
  };

  const handleDownload = async (data) => {
    try {
      setDownload(true);

      const { salemode, saleno } = data || {};
      let doc;

      if (salemode?.toUpperCase() === "BILL") {
        doc = await generateBill1();
      } else {
        doc = await generateInvoice1();
      }

      const filenamePrefix = formatTrackMode(salemode)?.replace(/\s+/g, "_");

      // 🔥 Fast direct download (no preview)
      doc.save(`${filenamePrefix}_${saleno}.pdf`);

      // ✅ Close tab after small delay
      setTimeout(() => {
        window.close();
      }, 100);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownload(false);
    }
  };

  useEffect(() => {
    jsPDF.API.events.push([
      "addFonts",
      function () {
        this.addFileToVFS("Inter_24pt-Regular-normal.ttf", InterRegular);
        this.addFileToVFS("Inter_24pt-Bold-normal.ttf", InterBold);
        this.addFont(
          "Inter_24pt-Regular-normal.ttf",
          "Inter-Regular",
          "normal"
        );
        this.addFont("Inter_24pt-Bold-normal.ttf", "Inter-Bold", "bold");
      },
    ]);
  }, []);

  useEffect(() => {
    if (data?.saleno) {
      handleDownload(data);
    }
  }, [data?.saleno]);



  useEffect(() => {
    setData(isData);
  }, [isData]);


  useEffect(() => {
    const upi = globalData?.storeSettings?.paymentSettings?.upiId;

    const name = globalData?.storeSettings?.paymentSettings?.bankDetails?.beneficiaryName;
    if (!upi) return; // skip until upi is available

    const upiUrl = `upi://pay?pa=${upi}&pn=${encodeURIComponent(name)}&cu=INR`;
    console.log("ip", upiUrl);
    QRCode.toDataURL(upiUrl)
      .then((url) => {
        setQrCodeDataUrl(url);
      })
      .catch((err) => {
        console.error("QR Code generation failed: ", err);
      });
  }, [
    globalData?.storeSettings?.paymentSettings?.upiId,
    globalData?.storeSettings?.paymentSettings?.bankDetails?.beneficiaryName,
  ]);
  const grantotal = CurrencyConverter.toCurrency(data?.grandtotal || 0, "INR");
  const paidamount = CurrencyConverter.toCurrency(data?.paidamount || 0, "INR");
  const balanceDue = grantotal - paidamount;
  const isBillReceipt =
    globalData?.salemode?.toUpperCase() === "BILL" &&
    globalData?.storeSettings?.saleSettings?.saleModeSettings?.isTaxInfo;

  const documentTitle = isBillReceipt
    ? "BILL RECEIPT"
    : "TAX INVOICE";
  const invLabel = isBillReceipt ? "Bill #:" : "Invoice #:";
  const invDate = isBillReceipt ? "Bill Date:" : "Invoice Date:";
  const invTimes = isBillReceipt ? "Bill Time:" : "Invoice Time:";
  
  return (
    <>
   
           <InvoiceLayout>
                          {globalData?.salemode === "INVOICE" || isBillReceipt  ? (
                        <>
                         
                           <InvoiceWrapper>
                                  <div className="tm_invoice_in p-2">
                                    <div className="d-flex justify-content-between mb-2">
                                      <div className="tm_logo text-start">
                                        {globalData?.salefrom?.image && (
                                          <img
                                            src={
                                              uRLs.IMAGE_URL +
                                              "profile/" +
                                              globalData?.salefrom?.image
                                            }
                                            className="img-rounded logo mb-0"
                                            height="45"
                                            width="100"
                                            style={{ objectFit: "contain" }}
                                          />
                                        )}
                                      </div>
                                      <div className="text-end">
                                        <div className="font-16">
                                         {documentTitle}
                                          <div>
                                            <span className="font-12 fw-600">
                                             {invLabel}
                                            </span>
                                            {""}
                                            <strong className="font-12">
                                              {globalData?.saleno}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col-12 col-md-5 pe-1 ">
                                        <div className="d-flex gap-2 align-items-start">
                                          <div>
                                            <div className="font-14 mb- fw-bold">
                                              {globalData.salefrom?.title || ""}
                                            </div>
                                            {globalData?.salefrom
                                              ?.gstnumber && (
                                              <div className="font-11 fw-600 mb-0 ">
                                                <>GSTIN:</>{" "}
                                                {globalData?.salefrom
                                                  ?.gstnumber || ""}
                                              </div>
                                            )}
                                            <div className="font-11">
                                              {globalData?.salefrom
                                                ?.addressline1 || ""}{" "}
                                              {globalData?.salefrom
                                                ?.addressline2 || ""}{" "}
                                              {globalData?.salefrom?.district ||
                                                ""}
                                              {", "}
                                              {globalData?.salefrom?.state ||
                                                ""}
                                              {", "}
                                              {globalData?.salefrom?.pincode ||
                                                ""}
                                            </div>
                                            <div className="font-11">
                                              <strong>Mobile:</strong>{" "}
                                              {globalData?.salefrom?.phone ||
                                                ""}{" "}
                                              | <strong>Email:</strong>{" "}
                                              {globalData?.salefrom?.email ||
                                                "-"}
                                            </div>
                                            {globalData?.salefrom
                                              ?.pannumber && (
                                              <div className="font-11">
                                                <strong>PAN:</strong>{" "}
                                                {globalData?.salefrom
                                                  ?.pannumber || ""}
                                              </div>
                                            )}
                                            {globalData?.salefrom?.fassi && (
                                              <div className="font-11">
                                                <strong>FASSI:</strong>{" "}
                                                {globalData?.salefrom?.fassi ||
                                                  ""}
                                              </div>
                                            )}
                                            {globalData?.salefrom
                                              ?.cinnumber && (
                                              <div className="font-11">
                                                <strong>CIN:</strong>{" "}
                                                {
                                                  globalData?.salefrom
                                                    ?.cinnumber
                                                }
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="row mt-2">
                                      <div className="col-12  col-md-4 ps-md-1 ">
                                        {globalData?.saleto &&
                                          (
                                            <>
                                              <div>
                                                <div>
                                                  <span className="fw-500">
                                                    Bill To:
                                                  </span>
                                                  {""}
                                                </div>
                                                <div className="font-12 mb-0">
                                                  <strong>
                                                    {globalData?.saleto?.name ||
                                                      ""}
                                                  </strong>
                                                </div>
                                                {globalData?.saleto
                                                  ?.gstnumber && (
                                                  <div className="font-11">
                                                    <strong>GSTIN:</strong>{" "}
                                                    {globalData.saleto
                                                      ?.gstnumber || ""}
                                                  </div>
                                                )}
                                                <div className="font-11">
                                                  {globalData?.saleto
                                                    ?.addressline1 || ""}{" "}
                                                  {globalData?.saleto
                                                    ?.addressline2 || ""}{" "}
                                                </div>
                                                <div className="font-11">
                                                  {globalData?.saleto?.state ||
                                                    ""}{" "}
                                                  {globalData?.saleto
                                                    ?.district || ""}{" "}
                                                  {globalData?.saleto
                                                    ?.country || ""}{" "}
                                                  {globalData?.saleto
                                                    ?.pincode || ""}
                                                </div>

                                                {globalData?.saleto?.phone && (
                                                  <div className="font-11">
                                                    {" "}
                                                    Ph:{" "}
                                                    {globalData?.saleto
                                                      ?.phone || ""}{" "}
                                                    | Email:{" "}
                                                    {globalData?.saleto
                                                      ?.email || ""}
                                                  </div>
                                                )}
                                                {/* <div> Email {globalData.saleto?.email || ""}</div> */}

                                                {globalData.saleto
                                                  ?.pannumber && (
                                                  <div className="font-11">
                                                    <strong>PAN:</strong>{" "}
                                                    {globalData.saleto
                                                      ?.pannumber || ""}
                                                  </div>
                                                )}
                                                {globalData.saleto
                                                  ?.cinnumber && (
                                                  <div className="font-11">
                                                    <strong>CIN :</strong>
                                                    {globalData?.saleto
                                                      ?.cinnumber || ""}
                                                  </div>
                                                )}
                                              </div>
                                            </>
                                          )}
                                      </div>
                                      { globalData.saleto?.shippingAddress &&
                                        (globalData.saleto?.shippingAddress
                                          ?.addressline1 ||
                                          globalData.saleto?.shippingAddress
                                            ?.addressline2) && (
                                          <>
                                            <div className="col-12 col-md-5 ps-md-1 ms-auto">
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  // fontWeight: "600",
                                                  marginBottom: "2px",
                                                }}
                                              >
                                                Shipping Address
                                              </div>
                                              <div>
                                                {globalData?.saleto
                                                  ?.shippingAddress
                                                  ?.addressline1 || ""}{" "}
                                                {globalData?.saleto
                                                  ?.shippingAddress
                                                  ?.addressline2 || ""}{" "}
                                              </div>
                                              <div>
                                                {globalData?.saleto
                                                  ?.shippingAddress?.state ||
                                                  ""}{" "}
                                                {globalData?.saleto
                                                  ?.shippingAddress?.district ||
                                                  ""}{" "}
                                                {globalData?.saleto
                                                  ?.shippingAddress?.country ||
                                                  ""}{" "}
                                                -{" "}
                                                {globalData?.saleto
                                                  ?.shippingAddress?.pincode ||
                                                  ""}
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      <div className="col-12 col-md-3 ps-md-1 ms-auto">
                                        {saleTime.date && (
                                          <>
                                            {/* justify-content-between pushes the value to the right end */}
                                            <div className="d-flex justify-content-between mb-0 font-11">
                                              <span className="fw-bold">
                                               {invDate}
                                              </span>
                                              <strong className="fw-semibold">
                                                {saleTime.date}
                                              </strong>
                                            </div>

                                            <div className="d-flex justify-content-between mb-0 font-11">
                                              <span className="fw-bold">
                                                {invTimes}
                                              </span>
                                              <strong className="fw-semibold">
                                                {
                                                  DateConverter.ISOToDisplayFormat(
                                                    DateConverter.convertToTimeZone(
                                                      globalData?.saledate,
                                                      timezone,
                                                    ),
                                                  ).time
                                                }
                                              </strong>
                                            </div>
                                          </>
                                        )}
                                        {globalData?.saleto?.state && (
                                          <>
                                            <div className="d-flex  font-11 justify-content-between mb-10">
                                              <span className="fw-bold">
                                                Place of Supply:
                                              </span>
                                              <strong className="fw-semibold text-end ">
                                                {globalData?.saleto?.state ||
                                                  ""}
                                              </strong>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                     <div
                                      style={{
                                        width: "100%",
                                        overflowX: "auto",
                                        WebkitOverflowScrolling: "touch",
                                      }}
                                    >
                                    <table
                                      // className="items"
                                      width="100%"
                                      style={{
                                        marginTop: "2px",
                                        borderCollapse: "collapse",
                                        // borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        // borderBottom: "1px solid #000"
                                      }}
                                      cellPadding="4"
                                    >
                                      <thead>
                                        <tr
                                        //  style={{
                                        //   borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        //   borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`
                                        // }}
                                        >
                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "20px",
                                              textAlign: "center",
                                            }}
                                          >
                                            #
                                          </th>
                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              // width: "240px",
                                              textAlign: "left",
                                            }}
                                          >
                                            Item
                                          </th>

                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "50px",
                                              textAlign: "right",
                                            }}
                                          >
                                            MRP
                                          </th>
                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "100px",
                                              textAlign: "right",
                                            }}
                                          >
                                            Selling Price
                                          </th>
                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "100px",
                                              textAlign: "right",
                                            }}
                                          >
                                            Qty
                                          </th>

                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "100px",
                                              textAlign: "right",
                                            }}
                                          >
                                            Amount
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {globalData?.saleitems?.map(
                                          (product, index) => (
                                            <tr
                                              className="align-top font-11"
                                              key={index}
                                              style={{
                                                borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                              }}
                                            >
                                              <td className="force-style align-left">
                                                {index + 1}
                                              </td>
                                              <td className="force-style align-left">
                                                <div className="font-11">
                                                  {product.title}
                                                </div>

                                                {product.hsnsac && (
                                                  <div>
                                                    <span className="fw-medium font-11">
                                                      {" "}
                                                      {product.imode ===
                                                      "SERVICE"
                                                        ? "SAC:"
                                                        : "HSN:"}
                                                    </span>{" "}
                                                    {product.hsnsac}
                                                  </div>
                                                )}
                                              </td>
                                              <td className="force-style text-end">
                                                ₹{product?.mrp}
                                              </td>
                                              <td className="force-style text-end">
                                                ₹{product?.baseprice}
                                              </td>
                                              <td className="force-style text-end">
                                                {product?.quantity}
                                                <span className="font-11 ms-1">
                                                  {(() => {
                                                    const unitCode =
                                                      product?.isSecondaryUnit
                                                        ? product?.unitConfig
                                                            ?.secondaryUnitCode
                                                        : product?.unitConfig
                                                            ?.primaryUnitCode;

                                                    // return unitCode !== "unit" ? unitCode : "";
                                                    return unitCode || "";
                                                  })()}
                                                </span>
                                              </td>

                                              <td className="force-style text-end">
                                                ₹{product?.lineGrandTotal}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                    </div>
                                    <div className="row mt-2">
                                      <div className="col-12 col-md-4 pe-1">
                                        <div className="font-12">
                                          <strong className="mb-1">
                                            Bank Details:
                                          </strong>
                                          <br />

                                          <div className="d-flex font-11">
                                            <span style={{ width: "120px" }}>
                                              Name:
                                            </span>
                                            <strong>
                                              {globalData?.storeSettings?.paymentSettings
                                                ?.bankDetails
                                                ?.beneficiaryName || ""}
                                            </strong>
                                          </div>

                                          <div className="d-flex font-11">
                                            <span style={{ width: "120px" }}>
                                              Account #:
                                            </span>
                                            <strong>
                                              {globalData?.storeSettings?.paymentSettings
                                                ?.bankDetails?.accountNo || ""}
                                            </strong>
                                          </div>

                                          <div className="d-flex font-11">
                                            <span style={{ width: "120px" }}>
                                              IFSC:
                                            </span>
                                            <strong>
                                              {globalData?.storeSettings?.paymentSettings
                                                ?.bankDetails?.ifsc || ""}
                                            </strong>
                                          </div>

                                          <div className="d-flex font-11">
                                            <span style={{ width: "120px" }}>
                                              UPI ID:
                                            </span>
                                            <strong>
                                              {globalData?.storeSettings?.paymentSettings
                                                ?.upiId || ""}
                                            </strong>
                                          </div>

                                          <div className="mt-2 font-11">
                                            {`Total amount (in words) ${
                                              globalData?.amountinwords || ""
                                            }`}
                                          </div>
                                          <div className=" font-11">
                                            {globalData?.cashPaid > 0 && (
                                              <div className=" font-10 ps-1">
                                                <span>
                                                  <b>₹{globalData.cashPaid}</b>{" "}
                                                  Paid via Cash
                                                </span>
                                              </div>
                                            )}
                                            {globalData?.upiPaid > 0 && (
                                              <div className=" font-10 ps-1">
                                                <span>
                                                  <b>₹{globalData.upiPaid}</b>{" "}
                                                  Paid via UPI{" "}
                                                </span>
                                              </div>
                                            )}
                                            {globalData?.cardPaid > 0 && (
                                              <div className=" font-10 ps-1">
                                                <span>
                                                  <b>₹{globalData.cardPaid}</b>{" "}
                                                  Paid via Card{" "}
                                                </span>
                                              </div>
                                            )}
                                            {globalData?.onlinePaid > 0 && (
                                              <div className=" font-10 ps-1">
                                                <span>
                                                  <b>
                                                    ₹{globalData.onlinePaid}
                                                  </b>{" "}
                                                  Paid via Online{" "}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {/* CENTER – QR CODE */}
                                      <div className="col-12 col-md-2 pe-1 ">
                                        {globalData?.storeSettings?.paymentSettings
                                          ?.upiId && (
                                          <img
                                            src={qrCodeDataUrl}
                                            alt="QR Code"
                                            style={{
                                              width: "90px",
                                              height: "90px",
                                            }}
                                          />
                                        )}
                                      </div>

                                      <div className="col-12  col-md-4 pe-1">
                                        <div className="font-11">
                                          <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                            <span>Taxable Amount</span>
                                            <span>
                                              ₹{globalData?.totalbaseprice}{" "}
                                            </span>
                                          </div>
                                          {globalData?.salefrom?.statecode &&
                                          globalData?.saleto?.statecode &&
                                          globalData?.salefrom?.statecode !==
                                            globalData?.saleto?.statecode ? (
                                            <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                              <span>IGST</span>
                                              <span>
                                                ₹{globalData?.igstamount}{" "}
                                              </span>
                                            </div>
                                          ) : (
                                            <>
                                              <div className="d-flex justify-content-between   font-11 pe-sm-0-6">
                                                <span>CGST</span>
                                                <span>
                                                  ₹{globalData?.cgstamount}{" "}
                                                </span>
                                              </div>

                                              <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                                <span>SGST</span>
                                                <span>
                                                  ₹{globalData?.sgstamount}{" "}
                                                </span>
                                              </div>
                                            </>
                                          )}
                                          <div
                                            className="d-flex justify-content-between fw-bold mt-1 font-11 pe-sm-0-6"
                                            style={{
                                              backgroundColor:
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor, // light grey/blue
                                              padding: "4px 8px",

                                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                              borderRadius: "0px",
                                            }}
                                          >
                                            <span
                                              style={{
                                                color:
                                                  selectedTheme?.templateTheme
                                                    ?.foregroundColor,
                                              }}
                                            >
                                              Total
                                            </span>
                                            <span
                                              style={{
                                                color:
                                                  selectedTheme?.templateTheme
                                                    ?.foregroundColor,
                                              }}
                                            >
                                              ₹{globalData?.grandtotal}
                                            </span>
                                          </div>
                                          <div className="d-flex justify-content-between fw-bold font-11 pe-sm-0-6">
                                            <span>Amount Payable:</span>
                                            <span>
                                              ₹{globalData?.balanceDue}
                                            </span>
                                          </div>
                                          <div className="d-flex justify-content-between font-11 pe-sm-0-6">
                                            <span>Amount paid:</span>
                                            <span>
                                              ₹
                                              {parseFloat(
                                                actualPaidAmount || 0,
                                              ).toFixed(2)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="row mt-2">
                                      <div className="col-12 col-md-9 pe-1">
                                        {terms?.length > 0 && (
                                          <table width="100%">
                                            <tbody>
                                              <tr>
                                                <td width="100%">
                                                  <div
                                                    className="fs-12 fw-600"
                                                    style={{
                                                      textAlign: "left",
                                                      color: "black",
                                                    }}
                                                  >
                                                    Terms & Conditions
                                                  </div>
                                                  <div className="terms-box font-10">
                                                    {terms}
                                                  </div>
                                                </td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        )}
                                      </div>
                                      <div className="col-12 col-md-3 pe-2">
                                        <div>
                                          {globalData?.storeSettings?.invoiceSettings
                                            ?.signature && (
                                            <div className="text-end p-0 fw-bold ">
                                              <img
                                                src={
                                                  uRLs.IMAGE_URL +
                                                  globalData?.storeSettings?.invoiceSettings
                                                    ?.signature
                                                }
                                                width={130}
                                              />
                                            </div>
                                          )}
                                          <div className="text-end p-0">
                                            Authorized Signatory
                                          </div>
                                          {/* <div className="text-end p-0 fw-bold font-14">
                                          {data?.salefrom?.title}
                                        </div> */}
                                        </div>
                                      </div>
                                    </div>
                                    {globalData?.storeSettings?.negobillBranding && (
                                      <div className="d-flex justify-content-center">
                                        {" "}
                                        <span>
                                          Powered by{" "}
                                          <strong>NegoBill.com</strong>
                                        </span>
                                      </div>
                                    )}

                                    {/* <table
                            width="100%"
                            style={{ borderCollapse: "collapse" }}
                            cellPadding="8"
                          >
                            <tbody>
                            <tr>
                            {globalData?.storeSettings?.invoiceSettings?.signature && (
                                  <td className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        globalData?.storeSettings?.invoiceSettings?.signature
                                      }
                                      width={100}
                                    />
                                  </td> )}
          
                              </tr>
                              <tr>
                                <td className="text-end p-0">Issued By,Signature</td>
                              </tr>
                              <tr>
                                <td className="text-end p-0 fw-bold ">
                                  {settings?.saleProfileData?.title}
                                </td>
                              </tr>
                            </tbody>
                          </table> */}
                                  </div>
                             </InvoiceWrapper>
                         
                        </>
                      ) : globalData.salemode === "BILL" ? (
                        <>
                        
                            <InvoiceWrapper>
                                  <div className="tm_invoice_in p-2">
                                    <div className="d-flex justify-content-between mb-2">
                                      <div className="tm_logo text-start">
                                        {globalData?.salefrom?.image && (
                                          <img
                                            src={
                                              uRLs.IMAGE_URL +
                                              "profile/" +
                                              globalData?.salefrom?.image
                                            }
                                            className="img-rounded logo mb-0"
                                            height="45"
                                            width="100"
                                            style={{ objectFit: "contain" }}
                                          />
                                        )}
                                      </div>
                                      <div className="text-end">
                                        <div className="font-16">
                                          BILL RECEIPT
                                          <div>
                                            <span className="font-12 fw-600">
                                              Bill #:
                                            </span>
                                            {""}
                                            <strong className="font-12">
                                              {globalData.saleno}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col-4 pe-1 ">
                                        <div className="d-flex gap-2 align-items-start">
                                          <div>
                                            <div className="font-14 mb-1 fw-bold">
                                              {globalData.salefrom?.title || ""}
                                            </div>
                                            {globalData?.salefrom
                                              ?.gstnumber && (
                                              <div className="font-12 fw-600 mb-0">
                                                <>GSTIN:</>{" "}
                                                {globalData?.salefrom
                                                  ?.gstnumber || ""}
                                              </div>
                                            )}
                                            <div className="font-11">
                                              {globalData?.salefrom
                                                ?.addressline1 || ""}{" "}
                                              {globalData?.salefrom
                                                ?.addressline2 || ""}{" "}
                                              {globalData?.salefrom?.district ||
                                                ""}
                                              {", "}
                                              {globalData?.salefrom?.state ||
                                                ""}
                                              {", "}
                                              {globalData?.salefrom?.pincode ||
                                                ""}
                                            </div>
                                            <div font-11>
                                              <strong>Mobile:</strong>{" "}
                                              {globalData?.salefrom?.phone ||
                                                ""}{" "}
                                              | <strong>Email:</strong>{" "}
                                              {globalData?.salefrom?.email ||
                                                "-"}
                                            </div>
                                            {globalData?.salefrom
                                              ?.pannumber && (
                                              <div>
                                                <strong>PAN:</strong>{" "}
                                                {globalData?.salefrom
                                                  ?.pannumber || ""}
                                              </div>
                                            )}
                                            {globalData?.salefrom?.fassi && (
                                              <div>
                                                <strong>FASSI:</strong>{" "}
                                                {globalData?.salefrom?.fassi ||
                                                  ""}
                                              </div>
                                            )}
                                            {globalData?.salefrom
                                              ?.cinnumber && (
                                              <div>
                                                <strong>CIN:</strong>{" "}
                                                {
                                                  globalData?.salefrom
                                                    ?.cinnumber
                                                }
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="row mt-2 mb-1">
                                      <div className="col-4 pe-1">
                                        {globalData?.saleto &&
                                         (
                                            <>
                                              <div>
                                                <div>
                                                  <span className="fw-500">
                                                    Bill To:
                                                  </span>
                                                  {""}
                                                </div>
                                                <div className="font-12 mb-0">
                                                  <strong>
                                                    {globalData?.saleto?.name ||
                                                      ""}
                                                  </strong>
                                                </div>
                                                {globalData?.saleto
                                                  ?.gstnumber && (
                                                  <div className="font-11">
                                                    <strong>GSTIN:</strong>{" "}
                                                    {globalData.saleto
                                                      ?.gstnumber || ""}
                                                  </div>
                                                )}
                                                <div className="font-11">
                                                  {globalData?.saleto
                                                    ?.addressline1 || ""}{" "}
                                                  {globalData?.saleto
                                                    ?.addressline2 || ""}{" "}
                                                </div>
                                                <div className="font-11">
                                                  {globalData?.saleto?.state ||
                                                    ""}{" "}
                                                  {globalData?.saleto
                                                    ?.district || ""}{" "}
                                                  {globalData?.saleto
                                                    ?.country || ""}{" "}
                                                  {globalData?.saleto
                                                    ?.pincode || ""}
                                                </div>
                                                {/* {globalData?.saleto?.gstnumber && (
                                          <div className="font-11">
                                            <strong>GSTIN:</strong>{" "}
                                            {globalData.saleto?.gstnumber || ""}
                                          </div>
                                           )}  */}
                                                {globalData?.saleto?.phone && (
                                                  <div className="font-11">
                                                    Ph:{" "}
                                                    {globalData?.saleto
                                                      ?.phone || ""}{" "}
                                                    | Email:{" "}
                                                    {globalData?.saleto
                                                      ?.email || ""}
                                                  </div>
                                                )}
                                                {/* <div> Email {globalData.saleto?.email || ""}</div> */}

                                                {globalData.saleto
                                                  ?.pannumber && (
                                                  <div className="font-11">
                                                    <strong>PAN:</strong>{" "}
                                                    {globalData.saleto
                                                      ?.pannumber || ""}
                                                  </div>
                                                )}
                                                {globalData.saleto
                                                  ?.cinnumber && (
                                                  <div className="font-11">
                                                    <strong>CIN :</strong>
                                                    {globalData?.saleto
                                                      ?.cinnumber || ""}
                                                  </div>
                                                )}
                                              </div>
                                            </>
                                          )}
                                      </div>
                                      {globalData.saleto?.shippingAddress &&                                      
                                        (globalData.saleto?.shippingAddress
                                          ?.addressline1 ||
                                          globalData.saleto?.shippingAddress
                                            ?.addressline2) && (
                                          <>
                                            <div className="col-5 pe-1">
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  // fontWeight: "600",
                                                  marginBottom: "2px",
                                                }}
                                              >
                                                Shipping Address
                                              </div>
                                              <div>
                                                {globalData?.saleto
                                                  ?.shippingAddress
                                                  ?.addressline1 || ""}{" "}
                                                {globalData?.saleto
                                                  ?.shippingAddress
                                                  ?.addressline2 || ""}{" "}
                                              </div>
                                              <div>
                                                {globalData?.saleto
                                                  ?.shippingAddress?.state ||
                                                  ""}{" "}
                                                {globalData?.saleto
                                                  ?.shippingAddress?.district ||
                                                  ""}{" "}
                                                {globalData?.saleto
                                                  ?.shippingAddress?.country ||
                                                  ""}{" "}
                                                -{" "}
                                                {globalData?.saleto
                                                  ?.shippingAddress?.pincode ||
                                                  ""}
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      <div className="col-3 ps-1 ms-auto">
                                        {/* justify-content-between pushes the value to the right end */}
                                        {saleTime.date && (
                                          <>
                                            <div className="d-flex justify-content-between mb-0 font-11">
                                              <span className="fw-bold">
                                                Bill Date:
                                              </span>
                                              <strong className="fw-semibold">
                                                {saleTime.date}
                                              </strong>
                                            </div>

                                            <div className="d-flex justify-content-between mb-0 font-11">
                                              <span className="fw-bold">
                                                Bill Time:
                                              </span>
                                              <strong className="fw-semibold">
                                                {
                                                  DateConverter.ISOToDisplayFormat(
                                                    DateConverter.convertToTimeZone(
                                                      globalData?.saledate,
                                                      timezone,
                                                    ),
                                                  ).time
                                                }
                                              </strong>
                                            </div>
                                          </>
                                        )}
                                        {globalData?.saleto?.state && (
                                          <div className="d-flex justify-content-between mb-10 font-11">
                                            <span className="fw-bold">
                                              Place of Supply:
                                            </span>
                                            <strong className="fw-semibold text-end">
                                              {globalData?.saleto?.state || ""}
                                            </strong>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                      <div
                                      style={{
                                        width: "100%",
                                        overflowX: "auto",
                                        WebkitOverflowScrolling: "touch",
                                      }}
                                    >
                                    <table
                                      // className="items"
                                      width="100%"
                                      style={{
                                        marginTop: "2px",
                                        borderCollapse: "collapse",
                                        // borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        // borderBottom: "1px solid #000"
                                      }}
                                      cellPadding="4"
                                    >
                                      <thead>
                                        <tr
                                        //  style={{
                                        //   borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        //   borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`
                                        // }}
                                        >
                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "20px",
                                              textAlign: "left",
                                            }}
                                          >
                                            #
                                          </th>
                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "auto",
                                              textAlign: "left",
                                            }}
                                          >
                                            Item
                                          </th>

                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "100px",
                                              textAlign: "right",
                                            }}
                                          >
                                            Selling Price
                                          </th>
                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "50px",
                                              textAlign: "right",
                                            }}
                                          >
                                            Qty
                                          </th>

                                          <th
                                            className="force-style p-1 fw-bold"
                                            style={{
                                              width: "100px",
                                              textAlign: "right",
                                            }}
                                          >
                                            Amount
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {globalData?.saleitems?.map(
                                          (product, index) => (
                                            <tr
                                              key={index}
                                              className="align-top font-11"
                                              style={{
                                                borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                              }}
                                            >
                                              <td className="force-style align-left">
                                                {index + 1}
                                              </td>
                                              <td className="force-style align-left ">
                                                <div className="font-11">
                                                  {product.title}
                                                </div>

                                                {product.hsnsac && (
                                                  <div>
                                                    <span className="fw-medium font-11">
                                                      {" "}
                                                      {product.imode ===
                                                      "SERVICE"
                                                        ? "SAC:"
                                                        : "HSN:"}
                                                    </span>{" "}
                                                    {product.hsnsac}
                                                  </div>
                                                )}
                                              </td>

                                              <td className="force-style text-end">
                                                ₹{product?.baseprice}
                                              </td>
                                              <td className="force-style text-end">
                                                {product?.quantity}
                                                <span className="font-11 ms-1">
                                                  {(() => {
                                                    const unitCode =
                                                      product?.isSecondaryUnit
                                                        ? product?.unitConfig
                                                            ?.secondaryUnitCode
                                                        : product?.unitConfig
                                                            ?.primaryUnitCode;

                                                    // return unitCode !== "unit" ? unitCode : "";
                                                    return unitCode || "";
                                                  })()}
                                                </span>
                                              </td>

                                              <td className="force-style text-end">
                                                {" "}
                                                ₹{product?.lineGrandTotal}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                    </div>
                                    <div className="row mt-2">
                                      <div className="col-6 pe-1">
                                        <div className="font-12">
                                          <strong className="mb-1">
                                            Bank Details:
                                          </strong>
                                          <br />

                                          <div className="d-flex font-11">
                                            <span style={{ width: "120px" }}>
                                              Name:
                                            </span>
                                            <strong>
                                              {globalData?.storeSettings?.paymentSettings
                                                ?.bankDetails
                                                ?.beneficiaryName || ""}
                                            </strong>
                                          </div>

                                          <div className="d-flex font-11">
                                            <span style={{ width: "120px" }}>
                                              Account #:
                                            </span>
                                            <strong>
                                              {globalData?.storeSettings?.paymentSettings
                                                ?.bankDetails?.accountNo || ""}
                                            </strong>
                                          </div>

                                          <div className="d-flex font-11">
                                            <span style={{ width: "120px" }}>
                                              IFSC:
                                            </span>
                                            <strong>
                                              {globalData?.storeSettings?.paymentSettings
                                                ?.bankDetails?.ifsc || ""}
                                            </strong>
                                          </div>

                                          <div className="d-flex font-11">
                                            <span style={{ width: "120px" }}>
                                              UPI ID:
                                            </span>
                                            <strong>
                                              {globalData?.storeSettings?.paymentSettings
                                                ?.upiId || ""}
                                            </strong>
                                          </div>

                                          <div className="mt-2 font-11">
                                            {`Total amount (in words) ${
                                              globalData?.amountinwords || ""
                                            }`}
                                          </div>
                                          <div className=" font-11">
                                            {globalData?.cashPaid > 0 && (
                                              <div className=" font-10 ">
                                                <span>
                                                  <b>₹{globalData.cashPaid}</b>{" "}
                                                  Paid via Cash
                                                </span>
                                              </div>
                                            )}
                                            {globalData?.upiPaid > 0 && (
                                              <div className=" font-10 ">
                                                <span>
                                                  <b>₹{globalData.upiPaid}</b>{" "}
                                                  Paid via UPI{" "}
                                                </span>
                                              </div>
                                            )}
                                            {globalData?.cardPaid > 0 && (
                                              <div className=" font-10">
                                                <span>
                                                  <b>₹{globalData.cardPaid}</b>{" "}
                                                  Paid via Card{" "}
                                                </span>
                                              </div>
                                            )}
                                            {globalData?.onlinePaid > 0 && (
                                              <div className=" font-10 ">
                                                <span>
                                                  <b>
                                                    ₹{globalData.onlinePaid}
                                                  </b>{" "}
                                                  Paid via Online{" "}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {/* CENTER – QR CODE */}
                                      <div className="col-2 pe-1 text-center">
                                        {globalData?.storeSettings?.paymentSettings
                                          ?.upiId && (
                                          <img
                                            src={qrCodeDataUrl}
                                            alt="QR Code"
                                            style={{
                                              width: "90px",
                                              height: "90px",
                                            }}
                                          />
                                        )}
                                      </div>

                                      <div className="col-4 pe-1">
                                        <div className="font-11">
                                          <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                            <span>Taxable Amount</span>
                                            <span>
                                              ₹{globalData?.totalbaseprice}{" "}
                                            </span>
                                          </div>

                                          <div
                                            className="d-flex justify-content-between fw-bold mt-2 font-11 pe-sm-0-6 "
                                            style={{
                                              backgroundColor:
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor, // light grey/blue
                                              padding: "4px 8px",

                                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                              borderRadius: "0px",
                                            }}
                                          >
                                            <span
                                              style={{
                                                color:
                                                  selectedTheme?.templateTheme
                                                    ?.foregroundColor,
                                              }}
                                            >
                                              Total
                                            </span>
                                            <span
                                              style={{
                                                color:
                                                  selectedTheme?.templateTheme
                                                    ?.foregroundColor,
                                              }}
                                            >
                                              ₹{globalData?.grandtotal}
                                            </span>
                                          </div>
                                          <div className="d-flex justify-content-between fw-bold mt-1 font-11 pe-sm-0-6">
                                            <span>Amount Payable:</span>
                                            <span>
                                              {globalData?.balanceDue}
                                            </span>
                                          </div>
                                          <div className="d-flex justify-content-between font-11 pe-sm-0-6">
                                            <span>Amount paid:</span>
                                            <span>
                                              ₹
                                              {parseFloat(
                                                actualPaidAmount || 0,
                                              ).toFixed(2)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="row mt-2">
                                      <div className="col-9 pe-1">
                                        {terms?.length > 0 && (
                                          <table width="100%">
                                            <tbody>
                                              <tr>
                                                <td width="100%">
                                                  <div
                                                    className="fs-12 fw-600"
                                                    style={{
                                                      textAlign: "left",
                                                      color: "black",
                                                    }}
                                                  >
                                                    Terms & Conditions
                                                  </div>
                                                  <div className="terms-box font-10">
                                                    {terms}
                                                  </div>
                                                </td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        )}
                                      </div>
                                      <div className="col-3 pe-2">
                                        <div>
                                          {globalData?.storeSettings?.invoiceSettings
                                            ?.signature && (
                                            <div className="text-end p-0 fw-bold ">
                                              <img
                                                src={
                                                  uRLs.IMAGE_URL +
                                                  globalData?.storeSettings?.invoiceSettings
                                                    ?.signature
                                                }
                                                width={130}
                                              />
                                            </div>
                                          )}
                                          <div className="text-end p-0">
                                            Authorized Signatory
                                          </div>
                                          {/* <div className="text-end p-0 fw-bold font-14">
                                          {data?.salefrom?.title}
                                        </div> */}
                                        </div>
                                      </div>
                                    </div>
                                    {globalData?.storeSettings?.negobillBranding && (
                                      <div className="d-flex justify-content-center">
                                        {" "}
                                        <span>
                                          Powered by{" "}
                                          <strong>NegoBill.com</strong>
                                        </span>
                                      </div>
                                    )}
                                   
                                  </div>
                            </InvoiceWrapper>
                       
                        </>
                      ) : (
                        <div className="default-content">
                          <p>No content available for this sale mode.</p>
                        </div>
                      )}
             </InvoiceLayout>  
   
    </>
  );
};

export default ServiceInvoice;
